import { useState, useRef } from 'react';
import { sandhillsRequest, focusErroredField, insertURLParameter } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import TranslationText from '@Components/Utilities/TranslationText';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';

const FBOVendorInformationView = (props) => {

    const [crmid, setCrmid] = useState(props.Crmid || null);
    const [formData, setFormData] = useState({
        airportIdentifier: props.AirportIdentifier || '',
        airportName: props.AirportName || '',
        vendorFuelID: 0,
        fuelType: 'Jet A',
        brand: '',
        price: '',
        currencyCode: props.CurrencyCode || 'USD',
        fuelComment: '',
        checkSum: ''
    });

    const [vendorFuelList, setVendorFuelList] = useState(props.VendorFuelList || []);
    const [vendorSuccess, setVendorSuccess] = useState(false);
    const [fuelSuccess, setFuelSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [vendorValidationErrors, setVendorValidationErrors] = useState({});
    const [fuelValidationErrors, setFuelValidationErrors] = useState({});
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);

    const submitVendorForm = () => {
        if (!isSubmitting) {
            let errors = run(formData, [
                ruleRunner('airportIdentifier', 'AirportIdentifier', ValidationRules.required),
                ruleRunner('airportName', 'AirportName', ValidationRules.required)
            ]);

            setVendorSuccess(false);
            setVendorValidationErrors(errors);
            focusErroredField(errors, inputReferences);

            if (Object.keys(errors).length <= 0) {
                setIsSubmitting(true);
                sandhillsRequest({
                    url: routes.SaveFBOVendor,
                    method: 'post',
                    data: {
                        Crmid: crmid,
                        AirportIdentifier: formData.airportIdentifier,
                        AirportName: formData.airportName,
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((response) => {
                    setIsSubmitting(false);
                    if (response && response.Success) {
                        setVendorSuccess(true);
                    } else {
                        setVendorValidationErrors(response.ValidationErrors || {});
                        focusErroredField(response.ValidationErrors, inputReferences);
                    }
                });
            }
        }
    };

    const submitFuelForm = () => {
        let errors = run(formData, [
            ruleRunner('fuelType', 'FuelType', ValidationRules.required),
            ruleRunner('brand', 'Brand', ValidationRules.required),
            ruleRunner('price', 'Price', ValidationRules.required),
            ruleRunner('price', 'Price', ValidationRules.isNumeric),
            ruleRunner('price', 'Price', ValidationRules.mustBeGreaterThanEqualToValue(0.1)),
            ruleRunner('currencyCode', 'CurrencyCode', ValidationRules.required)
        ]);

        setFuelSuccess(false)
        setFuelValidationErrors(errors);
        focusErroredField(errors, inputReferences);

        if (Object.keys(errors).length <= 0) {
            setIsSubmitting(true);
            sandhillsRequest({
                url: routes.SaveFBOFuel,
                method: 'post',
                data: {
                    Crmid: crmid,
                    VendorFuelID: formData.vendorFuelID,
                    FuelType: formData.fuelType,
                    Brand: formData.brand,
                    Price: formData.price,
                    CurrencyCode: formData.currencyCode,
                    FuelComment: formData.fuelComment,
                    CheckSum: formData.checkSum,
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                setIsSubmitting(false);
                if (response && response.Success) {
                    let newFuel = {
                        VendorFuelID: response.VendorFuelID,
                        FuelType: response.FuelType,
                        Brand: response.Brand,
                        Price: response.Price,
                        FormattedPrice: response.FormattedPrice,
                        CurrencyCode: response.CurrencyCode.trim(),
                        FuelComment: response.FuelComment,
                        CheckSum: response.CheckSum
                    };

                    if (vendorFuelList.some(fuel => fuel.VendorFuelID == newFuel.VendorFuelID)) {
                        setVendorFuelList(previousState => {
                            return previousState.map(fuel => {
                                if (fuel.VendorFuelID == newFuel.VendorFuelID) {
                                    return newFuel;
                                } else {
                                    return fuel;
                                }
                            });
                        });
                    } else {
                        setVendorFuelList(previousState => [...previousState, newFuel]);
                    }
                    clearFuelForm(true);
                } else {
                    setFuelValidationErrors(response.ValidationErrors || {});
                    focusErroredField(errors, inputReferences);
                }
            });
        }
    };

    const clearFuelForm = (success) => {
        setFormData(previousState => {
            return {
                ...previousState,
                vendorFuelID: 0,
                fuelType: 'Jet A',
                brand: '',
                price: '',
                currencyCode: props.CurrencyCode || 'USD',
                fuelComment: '',
                checkSum: ''
            };
        });

        setFuelSuccess(success);
        setFuelValidationErrors({});
        inputReferences.current['fuelType'].focus();
    };

    const editFuel = (model) => {
        setFormData(previousState => {
            return {
                ...previousState,
                vendorFuelID: model.VendorFuelID,
                fuelType: model.FuelType,
                brand: model.Brand,
                price: model.Price,
                currencyCode: model.CurrencyCode.trim(),
                fuelComment: model.FuelComment,
                checkSum: model.CheckSum
            };
        });

        setFuelSuccess(false);
        setFuelValidationErrors({});
        inputReferences.current['fuelType'].focus();
    };

    const deleteFuel = (model) => {
        setFormData(previousState => {
            return { ...previousState, vendorFuelID: 0 };
        });

        let url = routes.DeleteFBOFuel
        url = insertURLParameter(url, 'vendorFuelID', model.VendorFuelID);
        url = insertURLParameter(url, 'checkSum', model.CheckSum);

        if (crmid) {
            url = insertURLParameter(url, 'crmid', crmid)
        }

        sandhillsRequest({
            url: url,
            method: 'post',
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then(() => {
            setVendorFuelList(previousState => previousState.filter(fuel => fuel.VendorFuelID != model.VendorFuelID));
        });
    };

    const crmidLookup = () => {
        window.location = window.location.pathname + '?Crmid=' + crmid;
    };

    const enterCrmidLookup = (e) => {
        if (e.key === 'Enter') {
            crmidLookup();
        }
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    return (
        <div className='fbo-vendor-information'>
            {vendorValidationErrors && <ValidationErrors errors={vendorValidationErrors} />}
            {fuelValidationErrors && <ValidationErrors errors={fuelValidationErrors} />}
            <h3 className='uppercase'><span>UPDATE FBO VENDOR INFORMATION</span></h3>
            {props.IsEmployee && <div className='grey-box'>
                <div className='cf'>
                    <div className='column6 left'>
                        <label className='bold' htmlFor='VendorName'>CRMID Lookup <span className='required red' /></label>
                        <input type='text' className='fbo-input' id='VendorName' name='crmid' onChange={(e) => setCrmid(e.target.value)} onKeyDown={enterCrmidLookup} value={crmid} />
                        <span className='bold'>Vendor: </span> {props.VendorName ? props.VendorName : <span className='error'>No vendor exists for that CRMID.</span>}
                    </div>
                </div>
                <div>
                    <a className='account-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); crmidLookup(); } }} onClick={crmidLookup}>
                        <span>Get Vendor</span>
                    </a>
                </div>
            </div>}
            <div className='grey-box'>
                <div className='cf'>
                    <div className='column6 left'>
                        <label htmlFor='airportIdentifier'>
                            <strong>Airport Identifier <span className='required red' />
                            <div className='fbo-input-description'>
                                Three character Airport Code.<br />
                                    Ex: Omaha, NE is OMA<br />
                                    Please Validate: <a href='https://airportcodes.org/' target='_blank' aria-label={'https://airportcodes.org/ (' + translator.translate('sandhills.opensinanewtab') + ')'}>https://airportcodes.org/</a>
                            </div>
                            </strong>
                        </label>
                        <input type='text' onChange={onChange} name='airportIdentifier' id='airportIdentifier' value={formData.airportIdentifier} ref={(element) => inputReferences.current[`airportIdentifier`] = element}/>
                        <span className='error'>{vendorValidationErrors.airportIdentifier}</span>
                    </div>
                    <div className='column6 left'>
                        <label htmlFor='airportName'>
                            <strong>Airport Name <span className='required red' />
                            <div className='fbo-input-description'>
                                Please Validate: <a href='https://airportcodes.org/' target='_blank'>https://airportcodes.org/</a>
                            </div>
                            </strong>
                        </label>
                        <input type='text' onChange={onChange} name='airportName' id='airportName' value={formData.airportName} ref={(element) => inputReferences.current[`airportName`] = element} />
                        <span className='error'>{vendorValidationErrors.airportName}</span>
                    </div>
                </div>
                <div className='error center'>
                    {vendorValidationErrors.Failure}
                </div>
                {vendorSuccess && <div className='success center'>Vendor information successfully saved!</div>}
                <div>
                    <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); submitVendorForm(); } }} className='account-button' onClick={submitVendorForm}>
                        <span>Save Changes</span>
                    </a>
                </div>
            </div>
            <div className='grey-box'>
                <div className='alerts-header'>
                    <h4><span>Add A Fuel Price</span></h4>
                </div>
                <div className='cf'>
                    <div className='column6 left'>
                        <label className='bold' htmlFor='fuelType'> Fuel Type <span className='required red' /></label>
                        <select className='fbo-input' onChange={onChange} name='fuelType' id='fuelType' value={formData.fuelType} ref={(element) => inputReferences.current[`fuelType`] = element}>
                            <option value='Jet A'>Jet A</option>
                            <option value='Jet B'>Jet B</option>
                            <option value='Avgas 100LL'>Avgas 100LL</option>
                        </select>
                        <span className='error'>{fuelValidationErrors.fuelType}</span>
                    </div>
                    <TextField
                        name='brand'
                        value={formData.brand}
                        type='text'
                        label='Fuel Brand'
                        class='column6 left'
                        inputClass='fbo-input'
                        required={true}
                        validationErrors={fuelValidationErrors.brand}
                        ref={(element) => inputReferences.current[`brand`] = element}
                        onFieldChanged={onChange}
                    />
                </div>
                <div className='cf'>
                    <TextField
                        name='price'
                        value={formData.price}
                        type='text'
                        label='Price'
                        class='column4 left'
                        inputClass='fbo-input'
                        required={true}
                        validationErrors={fuelValidationErrors.price}
                        ref={(element) => inputReferences.current[`price`] = element}
                        onFieldChanged={onChange}
                    />
                    <div className='column2 left'>
                        <label className='bold' htmlFor='currencyCode'> <TranslationText tKey='sandhills.tradepubwebsite.mvc.currency' /> <span className='required red' /></label>
                        <select className='fbo-input' ref={(element) => inputReferences.current[`currencyCode`] = element} onChange={onChange} name='currencyCode' id='currencyCode' value={formData.currencyCode}>
                            {props.CurrencyCodeOptions && props.CurrencyCodeOptions.length > 0 ? props.CurrencyCodeOptions.map((currencyCode, index) => (
                                <option key={index} value={currencyCode}>{currencyCode}</option>
                            )) : <option value='USD'>USD</option>}
                        </select>
                        <span className='error'>{fuelValidationErrors.currencyCode}</span>
                    </div>
                    <TextField
                        name='fuelComment'
                        value={formData.fuelComment}
                        type='text'
                        label='Fuel Comments'
                        class='column6 left'
                        inputClass='fbo-input'
                        required={false}
                        validationErrors={fuelValidationErrors.fuelComment}
                        ref={(element) => inputReferences.current[`fuelComment`] = element}
                        onFieldChanged={onChange}
                    />
                </div>
                <div className='error center'>
                    {fuelValidationErrors.Failure}
                </div>
                {fuelSuccess && <div className='success center'>Fuel information successfully saved!</div>}
                <div className='cf'>
                    <div className='fuel-btn-container'>
                        <button className={'ts-button save-btn' + (isSubmitting ? ' disabled-btn' : '')} disabled={isSubmitting} onClick={submitFuelForm}><TranslationText tKey='sandhillssystem.save' /></button>
                        <button className='ts-button cancel-btn' onClick={() => clearFuelForm(false)}><TranslationText tKey='sandhills.cancel' /></button>
                    </div>
                </div>
            </div>
            <div className='email-alerts-list-box grey-box'>
                <div className='alerts-header'>
                    <h2 className='email-alerts-list__heading'>My Fuel Prices</h2>
                    <button className='account-button' onClick={() => clearFuelForm(false)}><i className='fas fa-plus'></i> Add Another Fuel Price</button>
                </div>
                <table className='alert-table'>
                    <tbody>
                        <tr className='alert-table-header'>
                            <th className='item-label'>Type</th>
                            <th>Brand</th>
                            <th>Price</th>
                            <th className='th-space'>&nbsp;</th>
                        </tr>
                        {vendorFuelList && vendorFuelList.length > 0 && vendorFuelList.map(fuel =>
                            <tr className='alert-table-data-row' key={fuel.VendorFuelID}>
                                <td className='cat-mm-td'>{fuel.FuelType}</td>
                                <td>{fuel.Brand}</td>
                                <td>{fuel.CurrencyCode + ' ' + fuel.FormattedPrice}</td>
                                <td>
                                    <div className='wtb-update-delete'>
                                        <span className='wtb-edit alert-table-btn'><a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); editFuel(fuel); } }} onClick={() => editFuel(fuel)}><i className='fas fa-edit'></i> Edit</a></span>
                                        <span className='wtb-delete alert-table-btn'><a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); deleteFuel(fuel); } }} onClick={() => deleteFuel(fuel)}><i className='fas fa-trash'></i> Delete</a></span>
                                    </div>
                                </td>
                            </tr>)}
                    </tbody>
                </table>
                <div className='alert-table-mobile'>
                    {vendorFuelList && vendorFuelList.length > 0 &&
                        <div>
                            {vendorFuelList.map(fuel =>
                                <div className='alert-table-data-row' key={fuel.VendorFuelID}>
                                    <div className='row-item'><strong>Type:</strong> {fuel.FuelType}</div>
                                    <div className='row-item'><strong>Brand:</strong> {fuel.Brand}</div>
                                    <div className='row-item'><strong>Price:</strong> {fuel.CurrencyCode + ' ' + fuel.FormattedPrice}</div>
                                    <div className='wtb-update-delete'>
                                        <span className='wtb-edit alert-table-btn'><a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); editFuel(fuel); } }} onClick={() => editFuel(fuel)}><i className='fas fa-edit'></i> Edit</a></span>
                                        <span className='wtb-delete alert-table-btn'><a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); deleteFuel(fuel); } }} onClick={() => deleteFuel(fuel)}><i className='fas fa-trash'></i> Delete</a></span>
                                    </div>
                                </div>)}
                        </div>}
                </div>
            </div>
            {isSubmitting && <LoadingRing backdrop />}
        </div>
    );
};

export default FBOVendorInformationView;
global.FBOVendorInformationView = FBOVendorInformationView;